/**
 * Persists which submitted story an admin is editing so refresh still targets that submission
 * (wizard navigations often drop ?sessionId= from the URL; currentStorySessionId is the admin's own draft).
 */
export const ADMIN_WORK_SESSION_STORAGE_KEY = "mysipur_admin_submission_session_id";

const ADMIN_SUBMISSION_WIZARD_PREFIXES = [
  "/create/story-studio",
  "/create/photo-training",
  "/create/character-analysis",
  "/create/image-studio",
  "/create/cover-studio",
  "/create/result",
  "/create/digital-book",
] as const;

export function isAdminSubmissionWorkPath(pathname: string): boolean {
  return ADMIN_SUBMISSION_WIZARD_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function readAdminWorkSessionId(): string | null {
  try {
    const s = sessionStorage.getItem(ADMIN_WORK_SESSION_STORAGE_KEY);
    return s?.trim() || null;
  } catch {
    return null;
  }
}

export function persistAdminWorkSessionId(sessionId: string): void {
  if (!sessionId?.trim()) return;
  try {
    sessionStorage.setItem(ADMIN_WORK_SESSION_STORAGE_KEY, sessionId.trim());
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearAdminWorkSessionStorage(): void {
  try {
    sessionStorage.removeItem(ADMIN_WORK_SESSION_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** For admin jump links: ensure `?sessionId=` from last “open submission” when missing. */
export function withStoredAdminSubmissionSession(fullPath: string): string {
  const qIndex = fullPath.indexOf("?");
  const pathname = qIndex === -1 ? fullPath : fullPath.slice(0, qIndex);
  const search = qIndex === -1 ? "" : fullPath.slice(qIndex + 1);
  if (!isAdminSubmissionWorkPath(pathname)) return fullPath;

  const sid = readAdminWorkSessionId()?.trim();
  if (!sid) return fullPath;

  const params = new URLSearchParams(search);
  const existing = params.get("sessionId")?.trim();
  if (existing) return fullPath;
  params.set("sessionId", sid);
  const next = params.toString();
  return next ? `${pathname}?${next}` : `${pathname}?sessionId=${encodeURIComponent(sid)}`;
}
